import { Injectable } from '@angular/core';

import { UserService } from 'src/app/core/services/admin/user.service';

export type ThemePreference = 'light' | 'dark' | 'system';

// 228 US9 (D-15) — the user's theme preference lives in viewState.theme and is applied as
// html[data-theme]. Only the weekplanner surfaces use the dark tokens so far; the default
// stays 'light' until the rest of the app (Material components) is themed for dark.
@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	preference: ThemePreference = 'light';
	private viewState: any = null;
	private media = window.matchMedia?.('(prefers-color-scheme: dark)');

	constructor(private userService: UserService) {
		this.media?.addEventListener?.('change', () => this.apply());
		this.userService.userProfileChanged.subscribe((profile) => {
			if (!profile || Object.keys(profile).length === 0) return;
			this.viewState = profile.viewState ? JSON.parse(JSON.stringify(profile.viewState)) : null;
			const stored = this.viewState?.theme;
			this.preference = stored === 'dark' || stored === 'system' || stored === 'light' ? stored : 'light';
			this.apply();
		});
	}

	get darkEnabled(): boolean {
		return ThemeService.DARK_ENABLED;
	}

	set(preference: ThemePreference) {
		this.preference = preference;
		this.apply();
		if (!this.viewState) return; // profile not loaded: never overwrite the whole viewState
		this.viewState = { ...this.viewState, theme: preference };
		this.userService.saveUserViewState(this.viewState).subscribe({ error: () => {} });
	}

	// Dark is switched off until the Material surfaces (cards, selects, menus) get a dark theme:
	// on 2026-10-16 only the weekplanner tokens flipped and Material cards stayed white (light
	// token text on white = unreadable). Decision pending with Wim; see the 228 status.
	static readonly DARK_ENABLED = false;

	private apply() {
		const wanted = this.preference === 'dark' || (this.preference === 'system' && !!this.media?.matches);
		const dark = ThemeService.DARK_ENABLED && wanted;
		document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
	}
}
